import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
var _excluded = ["indeterminate"],
    _excluded2 = ["className", "id", "title", "description", "fetchData", "emptyState", "filters", "action", "batchActions", "isLoading", "isSearchable", "isFilterable", "isSelectable", "verticalAlignment", "allowSingleSelect", "isSortable", "isExpandable", "hasTotal", "hasPagination", "hasEmptyResult", "isManualPagination", "isManualSort", "showFooter", "pageCount", "pageIndex", "sortBy", "hiddenColumns", "pageSizeOptions", "defaultPageSize", "searchText", "rowText", "rowsText", "canHideColumnBorder", "canHideBatchActionToolbar", "selectedText", "filteredText", "filtersText", "loadingText", "showPerPageText", "clearFiltersText", "pageText", "ofText", "selectText", "allSelectedText", "someSelectedText", "allText", "noOptionsText", "noResultsText", "subRows", "columns", "data", "total", "search", "selectedRows", "selectedRowsRaw", "onSearchChange", "onFiltersChange", "onSelectedRowsChange", "onPageIndexChange", "onSortChange", "renderRowSubComponent"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { useEffect, useRef, useState, useMemo, createContext, useContext } from 'react';
import * as React from 'react';
import cx from 'classnames';
import Skeleton from '../Skeleton';
import { prefix } from '../../tools/settings';
import { useTable, useSortBy, useExpanded, useRowSelect, usePagination, useFilters, useGlobalFilter, useMountedLayoutEffect } from 'react-table';
import Icon from '../Icon';
import IconButton from '../IconButton';
import EmptyState from '../EmptyState';
import Stack from '../Stack';
import Paragraph from '../Paragraph';
import FormatDateTime from '../FormatDateTime';
import FormatCurrency from '../FormatCurrency';
import FormatNumber from '../FormatNumber';
import Dropdown from '../Dropdown';
import Button from '../Button';
import Popover from '../Popover';
import Select from '../Select';
import FormField from '../FormField';
import Link from '../Link';
import Scrollable from '../Scrollable';
import Tooltip from '../Tooltip';
import { StyledTable, StyledTableTitle, StyledTableDescription, StyledTableContent, StyledTableContainer, StyledTableFilter, StyledTableFilters, StyledTableFilterContent, StyledTableFilterSeparator, StyledTableSuffix, StyledTablePrefix, StyledTablePagination, StyledTablePaginationOptions, StyledTablePaginationNavigation, StyledTablePaginationSelection, StyledTablePaginationButtons, StyledTableSearch, StyledTableToolbar, StyledTableToolbarText, StyledTableToolbarActions, StyledTableFilterButton, StyledTableAction } from './TableNext.styles';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { createElement as _createElement } from "react";
var TableContext = createContext();

var TableColumnFilter = function TableColumnFilter(_ref) {
  var label = _ref.label,
      _ref$type = _ref.type,
      type = _ref$type === void 0 ? 'single' : _ref$type,
      placeholder = _ref.placeholder,
      controlledOptions = _ref.options,
      selectText = _ref.selectText,
      noOptionsText = _ref.noOptionsText,
      allSelectedText = _ref.allSelectedText,
      someSelectedText = _ref.someSelectedText,
      onMenuClose = _ref.onMenuClose,
      isManualPagination = _ref.isManualPagination,
      _ref$column = _ref.column,
      filterValue = _ref$column.filterValue,
      setFilter = _ref$column.setFilter,
      preFilteredRows = _ref$column.preFilteredRows,
      id = _ref$column.id;
  var options = React.useMemo(function () {
    var options = new Set();

    if (!isManualPagination) {
      preFilteredRows.forEach(function (row) {
        options.add(row.values[id]);
      });
      return _toConsumableArray(options.values()).sort();
    } else {
      return options;
    }
  }, [id, isManualPagination, preFilteredRows]);

  var _useState = useState(),
      _useState2 = _slicedToArray(_useState, 2),
      selectedOption = _useState2[0],
      setSelectedOption = _useState2[1];

  useEffect(function () {
    if (filterValue === undefined) {
      setSelectedOption(null);
    }
  }, [filterValue]);

  var single = _jsx(Dropdown, {
    id: id,
    value: filterValue || '',
    onChange: function onChange(e) {
      setFilter(e.target.value || undefined);
    },
    style: {
      marginLeft: 8
    },
    label: label,
    placeholder: placeholder,
    children: controlledOptions ? controlledOptions.map(function (option, i) {
      return _jsx(Dropdown.Option, {
        value: option.value,
        children: option.label
      }, i);
    }) : options.map(function (option, i) {
      return _jsx(Dropdown.Option, {
        value: option,
        children: option
      }, i);
    })
  });

  var multiple = _jsx(FormField, {
    label: label,
    children: _jsx(Select, {
      id: id,
      hideSelectedOptions: true,
      isClearable: true,
      value: selectedOption,
      placeholder: selectText,
      menuPortalTarget: document.body,
      allSelectedMessage: allSelectedText,
      someSelectedMessage: someSelectedText,
      noOptionsMessage: function noOptionsMessage() {
        return "".concat(noOptionsText);
      },
      menuShouldBlockScroll: true,
      options: controlledOptions ? controlledOptions : options.map(function (option, i) {
        return {
          label: option,
          value: option
        };
      }),
      onMenuClose: onMenuClose,
      onChange: function onChange(selected, event) {
        setSelectedOption(selected);
        setFilter((selected === null || selected === void 0 ? void 0 : selected.value) || undefined);
      }
    })
  });

  return type === 'single' ? single : multiple;
};

var useInstance = function useInstance(instance) {
  var allColumns = instance.allColumns;
  var rowSpanHeaders = [];
  allColumns.forEach(function (column, i) {
    var id = column.id,
        allowRowSpan = column.allowRowSpan;

    if (allowRowSpan !== undefined) {
      rowSpanHeaders = [].concat(_toConsumableArray(rowSpanHeaders), [{
        id: id,
        topCellValue: null,
        topCellIndex: 0
      }]);
    }
  });
  Object.assign(instance, {
    rowSpanHeaders: rowSpanHeaders
  });
};

var TableFilter = function TableFilter(_ref2) {
  var preGlobalFilteredRows = _ref2.preGlobalFilteredRows,
      preFilteredRows = _ref2.preFilteredRows,
      globalFilter = _ref2.globalFilter,
      setGlobalFilter = _ref2.setGlobalFilter,
      i18n = _ref2.i18n,
      hasTotal = _ref2.hasTotal,
      total = _ref2.total,
      isManualPagination = _ref2.isManualPagination,
      isSearchable = _ref2.isSearchable,
      onSearchChange = _ref2.onSearchChange,
      setAllFilters = _ref2.setAllFilters,
      search = _ref2.search,
      filters = _ref2.filters,
      rows = _ref2.rows;
  var count = preFilteredRows.length;
  return _jsxs(_Fragment, {
    children: [isManualPagination ? _jsx("div", {
      children: hasTotal && _jsxs("div", {
        children: [total, ' ', (search || filters.length > 0) && _jsx("span", {
          className: "filtered",
          children: i18n.filtered
        }), ' ', total <= 1 ? i18n.row : i18n.rows, ' ', (search || filters.length > 0) && _jsx(Link, {
          style: {
            marginLeft: 8
          },
          onClick: function onClick() {
            onSearchChange && onSearchChange('');
            setAllFilters([]);
            setGlobalFilter(null);
          },
          children: i18n.clearFilters
        })]
      })
    }) : _jsx("div", {
      children: hasTotal && _jsxs("div", {
        children: [rows, ' ', rows < count && _jsx("span", {
          className: "filtered",
          children: i18n.filtered
        }), ' ', rows <= 1 ? i18n.row : i18n.rows, ' ', rows < count && _jsx(Link, {
          style: {
            marginLeft: 8
          },
          onClick: function onClick() {
            onSearchChange && onSearchChange('');
            setGlobalFilter(null);
            setAllFilters([]);
          },
          children: i18n.clearFilters
        })]
      })
    }), isSearchable && _jsx(StyledTableSearch, {
      value: isManualPagination ? search : globalFilter || '',
      placeholder: i18n.search,
      onChange: function onChange(e) {
        onSearchChange && onSearchChange(e.target.value);
        !isManualPagination && setGlobalFilter(e.target.value || undefined);
      }
    })]
  });
};

var IndeterminateCheckbox = React.forwardRef(function (_ref3, ref) {
  var indeterminate = _ref3.indeterminate,
      rest = _objectWithoutProperties(_ref3, _excluded);

  var _useContext = useContext(TableContext),
      allowSingleSelect = _useContext.allowSingleSelect;

  var defaultRef = useRef();
  var resolvedRef = ref || defaultRef;
  useEffect(function () {
    resolvedRef.current.indeterminate = indeterminate;
  }, [resolvedRef, indeterminate]);
  return allowSingleSelect ? _jsx("input", _objectSpread({
    type: "radio",
    ref: resolvedRef
  }, rest)) : _jsxs("label", {
    children: [_jsx("input", _objectSpread({
      type: "checkbox",
      ref: resolvedRef
    }, rest)), _jsx("span", {
      className: "checkmark"
    })]
  });
});

var colorDecision = function colorDecision(value, operator, against, decision) {
  switch (operator) {
    case '>':
    case 'gt':
      return value > against && decision;

    case '>=':
    case 'gte':
      return value >= against && decision;

    case '=':
    case 'eq':
      return value === against && decision;

    case '!=':
    case 'ne':
      return value !== against && decision;

    case '<=':
    case 'lte':
      return value <= against && decision;

    case '<':
    case 'lt':
      return value < against && decision;

    default:
      return undefined;
  }
};

var renderContent = function renderContent(value, type) {
  var _options$hasColor, _options$maximumFract, _options$minimumFract, _options$locale, _options$format, _options$hasColor2, _options$isCompact, _options$signDisplay, _options$mode, _options$format2, _options$isRelative, _options$size, _options$color;

  var options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};

  switch (type) {
    case 'boolean':
      return value === true ? options["true"] : _jsx("span", {
        style: {
          color: 'var(--dentsu-color-grey)'
        },
        children: options["false"]
      });

    case 'numeric':
      var customNumericColor = options.colorRange && (options.colorRange.positive && colorDecision(value, options.colorRange.positive.operator, options.colorRange.positive.value, 'positive') || options.colorRange.negative && colorDecision(value, options.colorRange.negative.operator, options.colorRange.negative.value, 'negative') || options.colorRange.neutral && colorDecision(value, options.colorRange.neutral.operator, options.colorRange.neutral.value, 'neutral'));
      return value === null ? '—' : _jsx(FormatNumber, {
        value: value,
        locale: options.locale,
        hasColor: (_options$hasColor = options.hasColor) !== null && _options$hasColor !== void 0 ? _options$hasColor : false,
        maximumFractionDigits: (_options$maximumFract = options.maximumFractionDigits) !== null && _options$maximumFract !== void 0 ? _options$maximumFract : 2,
        minimumFractionDigits: (_options$minimumFract = options.minimumFractionDigits) !== null && _options$minimumFract !== void 0 ? _options$minimumFract : 0,
        color: options.colorRange ? customNumericColor : undefined
      });

    case 'currency':
      var customCurrencyColor = options.colorRange && (options.colorRange.positive && colorDecision(value, options.colorRange.positive.operator, options.colorRange.positive.value, 'positive') || options.colorRange.negative && colorDecision(value, options.colorRange.negative.operator, options.colorRange.negative.value, 'negative') || options.colorRange.neutral && colorDecision(value, options.colorRange.neutral.operator, options.colorRange.neutral.value, 'neutral'));
      return value === null ? '—' : _jsx(FormatCurrency, {
        value: value,
        locale: (_options$locale = options.locale) !== null && _options$locale !== void 0 ? _options$locale : 'en-GB',
        currency: options.currency,
        format: (_options$format = options.format) !== null && _options$format !== void 0 ? _options$format : 'short',
        hasColor: (_options$hasColor2 = options.hasColor) !== null && _options$hasColor2 !== void 0 ? _options$hasColor2 : false,
        isCompact: (_options$isCompact = options.isCompact) !== null && _options$isCompact !== void 0 ? _options$isCompact : false,
        signDisplay: (_options$signDisplay = options.signDisplay) !== null && _options$signDisplay !== void 0 ? _options$signDisplay : 'auto',
        color: options.colorRange ? customCurrencyColor : undefined
      });

    case 'datetime':
      return value === null ? '—' : _jsx(FormatDateTime, {
        value: value,
        mode: (_options$mode = options.mode) !== null && _options$mode !== void 0 ? _options$mode : 'local',
        format: (_options$format2 = options.format) !== null && _options$format2 !== void 0 ? _options$format2 : 'datetime',
        isRelative: (_options$isRelative = options.isRelative) !== null && _options$isRelative !== void 0 ? _options$isRelative : false
      });

    case 'icon':
      return _jsx(Icon, {
        icon: options.icon,
        size: (_options$size = options.size) !== null && _options$size !== void 0 ? _options$size : 16,
        color: (_options$color = options.color) !== null && _options$color !== void 0 ? _options$color : 'var(--dentsu-color-n500)'
      });

    default:
      return value;
  }
};

var TableNext = React.forwardRef(function (_ref4, ref) {
  var _cx;

  var customClassName = _ref4.className,
      id = _ref4.id,
      title = _ref4.title,
      description = _ref4.description,
      fetchData = _ref4.fetchData,
      emptyState = _ref4.emptyState,
      _ref4$filters = _ref4.filters,
      controlledFilters = _ref4$filters === void 0 ? [] : _ref4$filters,
      action = _ref4.action,
      batchActions = _ref4.batchActions,
      _ref4$isLoading = _ref4.isLoading,
      isLoading = _ref4$isLoading === void 0 ? false : _ref4$isLoading,
      _ref4$isSearchable = _ref4.isSearchable,
      isSearchable = _ref4$isSearchable === void 0 ? false : _ref4$isSearchable,
      _ref4$isFilterable = _ref4.isFilterable,
      isFilterable = _ref4$isFilterable === void 0 ? false : _ref4$isFilterable,
      _ref4$isSelectable = _ref4.isSelectable,
      isSelectable = _ref4$isSelectable === void 0 ? false : _ref4$isSelectable,
      _ref4$verticalAlignme = _ref4.verticalAlignment,
      verticalAlignment = _ref4$verticalAlignme === void 0 ? 'middle' : _ref4$verticalAlignme,
      _ref4$allowSingleSele = _ref4.allowSingleSelect,
      allowSingleSelect = _ref4$allowSingleSele === void 0 ? false : _ref4$allowSingleSele,
      _ref4$isSortable = _ref4.isSortable,
      isSortable = _ref4$isSortable === void 0 ? false : _ref4$isSortable,
      _ref4$isExpandable = _ref4.isExpandable,
      isExpandable = _ref4$isExpandable === void 0 ? false : _ref4$isExpandable,
      _ref4$hasTotal = _ref4.hasTotal,
      hasTotal = _ref4$hasTotal === void 0 ? false : _ref4$hasTotal,
      _ref4$hasPagination = _ref4.hasPagination,
      hasPagination = _ref4$hasPagination === void 0 ? false : _ref4$hasPagination,
      _ref4$hasEmptyResult = _ref4.hasEmptyResult,
      hasEmptyResult = _ref4$hasEmptyResult === void 0 ? false : _ref4$hasEmptyResult,
      _ref4$isManualPaginat = _ref4.isManualPagination,
      isManualPagination = _ref4$isManualPaginat === void 0 ? false : _ref4$isManualPaginat,
      _ref4$isManualSort = _ref4.isManualSort,
      isManualSort = _ref4$isManualSort === void 0 ? false : _ref4$isManualSort,
      _ref4$showFooter = _ref4.showFooter,
      showFooter = _ref4$showFooter === void 0 ? false : _ref4$showFooter,
      controlledPageCount = _ref4.pageCount,
      _ref4$pageIndex = _ref4.pageIndex,
      controlledPageIndex = _ref4$pageIndex === void 0 ? 0 : _ref4$pageIndex,
      _ref4$sortBy = _ref4.sortBy,
      controlledSortBy = _ref4$sortBy === void 0 ? [] : _ref4$sortBy,
      _ref4$hiddenColumns = _ref4.hiddenColumns,
      controlledHiddenColumns = _ref4$hiddenColumns === void 0 ? [] : _ref4$hiddenColumns,
      _ref4$pageSizeOptions = _ref4.pageSizeOptions,
      pageSizeOptions = _ref4$pageSizeOptions === void 0 ? [10, 20, 30, 50, 100] : _ref4$pageSizeOptions,
      _ref4$defaultPageSize = _ref4.defaultPageSize,
      defaultPageSize = _ref4$defaultPageSize === void 0 ? 10 : _ref4$defaultPageSize,
      _ref4$searchText = _ref4.searchText,
      searchText = _ref4$searchText === void 0 ? 'Search' : _ref4$searchText,
      _ref4$rowText = _ref4.rowText,
      rowText = _ref4$rowText === void 0 ? 'row' : _ref4$rowText,
      _ref4$rowsText = _ref4.rowsText,
      rowsText = _ref4$rowsText === void 0 ? 'rows' : _ref4$rowsText,
      _ref4$canHideColumnBo = _ref4.canHideColumnBorder,
      canHideColumnBorder = _ref4$canHideColumnBo === void 0 ? false : _ref4$canHideColumnBo,
      _ref4$canHideBatchAct = _ref4.canHideBatchActionToolbar,
      canHideBatchActionToolbar = _ref4$canHideBatchAct === void 0 ? false : _ref4$canHideBatchAct,
      _ref4$selectedText = _ref4.selectedText,
      selectedText = _ref4$selectedText === void 0 ? 'selected' : _ref4$selectedText,
      _ref4$filteredText = _ref4.filteredText,
      filteredText = _ref4$filteredText === void 0 ? 'filtered' : _ref4$filteredText,
      _ref4$filtersText = _ref4.filtersText,
      filtersText = _ref4$filtersText === void 0 ? 'Filters' : _ref4$filtersText,
      _ref4$loadingText = _ref4.loadingText,
      loadingText = _ref4$loadingText === void 0 ? 'Loading' : _ref4$loadingText,
      _ref4$showPerPageText = _ref4.showPerPageText,
      showPerPageText = _ref4$showPerPageText === void 0 ? 'Show per page' : _ref4$showPerPageText,
      _ref4$clearFiltersTex = _ref4.clearFiltersText,
      clearFiltersText = _ref4$clearFiltersTex === void 0 ? 'Clear filters' : _ref4$clearFiltersTex,
      _ref4$pageText = _ref4.pageText,
      pageText = _ref4$pageText === void 0 ? 'Page' : _ref4$pageText,
      _ref4$ofText = _ref4.ofText,
      ofText = _ref4$ofText === void 0 ? 'of' : _ref4$ofText,
      _ref4$selectText = _ref4.selectText,
      selectText = _ref4$selectText === void 0 ? 'Select...' : _ref4$selectText,
      allSelectedText = _ref4.allSelectedText,
      someSelectedText = _ref4.someSelectedText,
      _ref4$allText = _ref4.allText,
      allText = _ref4$allText === void 0 ? 'All' : _ref4$allText,
      _ref4$noOptionsText = _ref4.noOptionsText,
      noOptionsText = _ref4$noOptionsText === void 0 ? null : _ref4$noOptionsText,
      _ref4$noResultsText = _ref4.noResultsText,
      noResultsText = _ref4$noResultsText === void 0 ? {
    title: 'No results found',
    description: 'Try adjusting your search or filter to find what you are looking for.'
  } : _ref4$noResultsText,
      _ref4$subRows = _ref4.subRows,
      subRows = _ref4$subRows === void 0 ? function (row) {
    return row.children;
  } : _ref4$subRows,
      columns = _ref4.columns,
      data = _ref4.data,
      total = _ref4.total,
      controlledSearch = _ref4.search,
      _ref4$selectedRows = _ref4.selectedRows,
      selectedRows = _ref4$selectedRows === void 0 ? {} : _ref4$selectedRows,
      _ref4$selectedRowsRaw = _ref4.selectedRowsRaw,
      selectedRowsRaw = _ref4$selectedRowsRaw === void 0 ? [] : _ref4$selectedRowsRaw,
      onSearchChange = _ref4.onSearchChange,
      onFiltersChange = _ref4.onFiltersChange,
      onSelectedRowsChange = _ref4.onSelectedRowsChange,
      onPageIndexChange = _ref4.onPageIndexChange,
      onSortChange = _ref4.onSortChange,
      _ref4$renderRowSubCom = _ref4.renderRowSubComponent,
      renderRowSubComponent = _ref4$renderRowSubCom === void 0 ? function (_ref5) {
    var row = _ref5.row;
    return null;
  } : _ref4$renderRowSubCom,
      rest = _objectWithoutProperties(_ref4, _excluded2);

  var className = cx((_cx = {}, _defineProperty(_cx, "".concat(prefix, "-table"), true), _defineProperty(_cx, customClassName, !!customClassName), _cx));

  var _useState3 = useState(false),
      _useState4 = _slicedToArray(_useState3, 2),
      popoverVisible = _useState4[0],
      setPopoverVisible = _useState4[1];

  var showPopover = function showPopover() {
    return setPopoverVisible(true);
  };

  var hidePopover = function hidePopover() {
    return setPopoverVisible(false);
  };

  var defaultColumn = React.useMemo(function () {
    return {
      Filter: TableColumnFilter
    };
  }, []);

  var _useTable = useTable({
    defaultColumn: defaultColumn,
    columns: columns,
    data: data,
    sortTypes: useMemo(function () {
      return {
        alphanumeric: function alphanumeric(a, b, id, desc) {
          var valueA = String(a.values[id]).toLowerCase();
          var valueB = String(b.values[id]).toLowerCase();

          if (valueA > valueB) {
            return 1;
          } else if (valueA < valueB) {
            return -1;
          } else {
            return 0;
          }
        }
      };
    }, []),
    disableFilters: !isFilterable,
    disableSortBy: !isSortable,
    disableGlobalFilter: isManualPagination,
    initialState: {
      globalFilter: !isManualPagination && controlledSearch,
      sortBy: controlledSortBy,
      selectedRowIds: selectedRows,
      hiddenColumns: controlledHiddenColumns,
      filters: controlledFilters,
      pageIndex: controlledPageIndex || 0,
      pageSize: hasPagination ? defaultPageSize : data.length
    },
    manualPagination: isManualPagination,
    manualSortBy: isManualSort,
    manualFilters: isManualPagination,
    pageCount: controlledPageCount,
    getSubRows: subRows || [],
    autoResetPage: isManualPagination ? false : true,
    autoResetSelectedRows: allowSingleSelect ? false : true
  }, useFilters, useGlobalFilter, useSortBy, useExpanded, usePagination, useRowSelect, function (hooks) {
    hooks.useInstance.push(useInstance);

    if (isSelectable) {
      hooks.allColumns.push(function (columns) {
        return [{
          id: 'selection',
          type: 'select',
          Header: function Header(_ref6) {
            var getToggleAllPageRowsSelectedProps = _ref6.getToggleAllPageRowsSelectedProps;
            return !allowSingleSelect && _jsx(IndeterminateCheckbox, _objectSpread({}, getToggleAllPageRowsSelectedProps()));
          },
          Cell: function Cell(_ref7) {
            var row = _ref7.row;
            return _jsx(IndeterminateCheckbox, _objectSpread({}, row.getToggleRowSelectedProps()));
          }
        }].concat(_toConsumableArray(columns));
      });
    }

    if (isExpandable) {
      hooks.allColumns.push(function (columns) {
        return [{
          id: 'expansion',
          type: 'expander',
          Header: function Header() {
            return null;
          },
          Cell: function Cell(_ref8) {
            var row = _ref8.row;
            return row.canExpand ? _jsx("span", _objectSpread(_objectSpread({}, row.getToggleRowExpandedProps()), {}, {
              children: row.isExpanded ? _jsx(Icon, {
                icon: "chevron-up",
                color: 'var(--dentsu-color-n500)'
              }) : _jsx(Icon, {
                icon: "chevron-down",
                color: 'var(--dentsu-color-n500)'
              })
            })) : renderRowSubComponent({
              row: row
            }) ? _jsx("span", _objectSpread(_objectSpread({}, row.getToggleRowExpandedProps()), {}, {
              children: row.isExpanded ? _jsx(Icon, {
                icon: "chevron-up",
                color: 'var(--dentsu-color-n500)'
              }) : _jsx(Icon, {
                icon: "chevron-down",
                color: 'var(--dentsu-color-n500)'
              })
            })) : null;
          }
        }].concat(_toConsumableArray(columns));
      });
    }
  }),
      getTableProps = _useTable.getTableProps,
      getTableBodyProps = _useTable.getTableBodyProps,
      headerGroups = _useTable.headerGroups,
      footerGroups = _useTable.footerGroups,
      rows = _useTable.rows,
      prepareRow = _useTable.prepareRow,
      page = _useTable.page,
      allColumns = _useTable.allColumns,
      canPreviousPage = _useTable.canPreviousPage,
      canNextPage = _useTable.canNextPage,
      pageOptions = _useTable.pageOptions,
      gotoPage = _useTable.gotoPage,
      nextPage = _useTable.nextPage,
      previousPage = _useTable.previousPage,
      setPageSize = _useTable.setPageSize,
      visibleColumns = _useTable.visibleColumns,
      preGlobalFilteredRows = _useTable.preGlobalFilteredRows,
      preFilteredRows = _useTable.preFilteredRows,
      setGlobalFilter = _useTable.setGlobalFilter,
      setSortBy = _useTable.setSortBy,
      rowSpanHeaders = _useTable.rowSpanHeaders,
      setAllFilters = _useTable.setAllFilters,
      _useTable$state = _useTable.state,
      selectedRowIds = _useTable$state.selectedRowIds,
      filters = _useTable$state.filters,
      globalFilter = _useTable$state.globalFilter,
      pageIndex = _useTable$state.pageIndex,
      pageSize = _useTable$state.pageSize,
      sortBy = _useTable$state.sortBy;

  useMountedLayoutEffect(function () {
    onPageIndexChange && onPageIndexChange(pageIndex);
  }, [onPageIndexChange, pageIndex]);
  useMountedLayoutEffect(function () {
    onSortChange && onSortChange(sortBy);
  }, [onSortChange, sortBy]);
  useEffect(function () {
    gotoPage(controlledPageIndex);
  }, [gotoPage, controlledPageIndex]);
  useEffect(function () {
    onSortChange && setSortBy(controlledSortBy);
  }, [onSortChange, setSortBy, controlledSortBy]);
  useEffect(function () {
    if (isManualPagination) {
      fetchData({
        pageIndex: pageIndex,
        pageSize: pageSize
      });
    }
  }, [fetchData, pageIndex, pageSize, isManualPagination]);
  useMountedLayoutEffect(function () {
    onFiltersChange && onFiltersChange(filters);
  }, [onFiltersChange, filters]);
  useMountedLayoutEffect(function () {
    if (isSelectable) {
      if (onSelectedRowsChange) {
        onSelectedRowsChange(selectedRowIds);
      }
    }
  }, [onSelectedRowsChange, selectedRowIds, isSelectable]);
  useMountedLayoutEffect(function () {
    if (onSearchChange && controlledSearch) {
      onSearchChange(controlledSearch);
    }
  }, [onSearchChange, controlledSearch]);
  useEffect(function () {
    if (!isManualPagination) {
      setGlobalFilter(controlledSearch);
    }
  }, [isManualPagination, controlledSearch, setGlobalFilter]);
  var context = {
    allowSingleSelect: allowSingleSelect
  };
  var rowData = hasPagination ? page : rows;
  var _selectedRows = Object.keys(selectedRowIds).length;
  var filteredColumns = allColumns.filter(function (column) {
    return column.allowFilter === true;
  }).length;
  return _jsx(TableContext.Provider, {
    value: context,
    children: _jsxs(StyledTable, _objectSpread(_objectSpread({
      ref: ref,
      className: className
    }, rest), {}, {
      children: [title && _jsx(StyledTableTitle, {
        className: "".concat(prefix, "-table-title"),
        children: title
      }), description && _jsx(StyledTableDescription, {
        className: "".concat(prefix, "-table-description"),
        children: description
      }), (isSearchable || hasTotal) && _jsxs(StyledTableFilter, {
        className: "".concat(prefix, "-table-filter"),
        children: [_jsx(StyledTableFilterContent, {
          children: _jsx(TableFilter, {
            preGlobalFilteredRows: preGlobalFilteredRows,
            preFilteredRows: preFilteredRows,
            globalFilter: globalFilter,
            setGlobalFilter: setGlobalFilter,
            setAllFilters: setAllFilters,
            i18n: {
              search: searchText,
              rows: rowsText,
              row: rowText,
              filtered: filteredText,
              clearFilters: clearFiltersText
            },
            isManualPagination: isManualPagination,
            hasTotal: hasTotal,
            isSearchable: isSearchable,
            total: total,
            search: controlledSearch,
            filters: controlledFilters,
            onSearchChange: onSearchChange,
            rows: isManualPagination ? total : rows.length
          })
        }), isFilterable && _jsx(StyledTableFilters, {
          className: "".concat(prefix, "-table-filters"),
          children: filteredColumns === 1 ? allColumns.map(function (column, i) {
            return column.allowFilter && _jsx(React.Fragment, {
              children: column.render('Filter', {
                label: column.render('Header'),
                isManualPagination: isManualPagination,
                placeholder: column.placeholder || allText,
                options: column.options
              })
            }, i);
          }) : _jsxs(_Fragment, {
            children: [_jsxs(Popover, {
              appendTo: "parent",
              placement: "bottom-end",
              width: 300,
              isVisible: popoverVisible,
              isCloseOnBlur: false,
              offset: [26, 15],
              trigger: "manual",
              children: [_jsx(Popover.Trigger, {
                children: _jsxs(StyledTableFilterButton, {
                  onClick: popoverVisible ? hidePopover : showPopover,
                  children: [filtersText, ' ', filters.length > 0 && "(".concat(filters.length, ")"), _jsx(Icon, {
                    style: {
                      marginLeft: 8
                    },
                    icon: "settings",
                    size: 14,
                    color: 'var(--dentsu-color-blue)'
                  })]
                })
              }), _jsx(Popover.Content, {
                children: allColumns.map(function (column, i) {
                  return column.allowFilter && _jsx(React.Fragment, {
                    children: column.render('Filter', {
                      label: column.render('Header'),
                      placeholder: column.placeholder || allText,
                      isManualPagination: isManualPagination,
                      selectText: selectText,
                      allSelectedText: allSelectedText,
                      someSelectedText: someSelectedText,
                      options: column.options,
                      noOptionsText: noOptionsText || column.noOptionsText || 'No options',
                      type: 'multiple'
                    })
                  }, i);
                })
              })]
            }), popoverVisible && _jsx("div", {
              onClick: hidePopover,
              style: {
                position: 'fixed',
                background: 'transparent',
                top: 0,
                right: 0,
                bottom: 0,
                left: 0,
                zIndex: 9998
              }
            })]
          })
        }), (isFilterable || isSearchable) && action && _jsx(StyledTableFilterSeparator, {}), action && _jsx(StyledTableAction, {
          className: "".concat(prefix, "-table-action"),
          children: _jsx(Button, {
            iconLeft: action.icon,
            variant: "tertiary",
            onClick: action.onClick,
            onBlur: action.onBlur,
            isDisabled: action.isDisabled,
            isLoading: action.isLoading,
            children: action.content
          })
        })]
      }), isSelectable && _selectedRows > 0 && !allowSingleSelect && !canHideBatchActionToolbar && _jsxs(StyledTableToolbar, {
        className: "".concat(prefix, "-table-toolbar"),
        children: [_jsxs(StyledTableToolbarText, {
          children: [_selectedRows, " ", _selectedRows <= 1 ? rowText : rowsText, ' ', selectedText]
        }), _jsx(StyledTableToolbarActions, {
          children: batchActions && batchActions.map(function (item, i) {
            return _jsx(Button, {
              mode: "dark",
              variant: item.variant || 'ghost',
              isDisabled: item.isDisabled,
              isLoading: item.isLoading,
              iconLeft: item.icon,
              onClick: item.onClick,
              onBlur: item.onBlur,
              children: item.content
            }, i);
          })
        })]
      }), _jsx(StyledTableContainer, {
        className: "".concat(prefix, "-table-container"),
        children: _jsx(Scrollable, {
          height: null,
          children: _jsxs(StyledTableContent, _objectSpread(_objectSpread({
            verticalAlignment: verticalAlignment
          }, getTableProps()), {}, {
            children: [_jsx("thead", {
              children: headerGroups.map(function (headerGroup) {
                return _jsx("tr", _objectSpread(_objectSpread({}, headerGroup.getHeaderGroupProps()), {}, {
                  children: headerGroup.headers.map(function (column, i) {
                    var _cx2;

                    return _createElement("th", _objectSpread(_objectSpread({}, column.getHeaderProps(column.allowSort && column.getSortByToggleProps())), {}, {
                      "data-type": column.type || 'text',
                      key: i,
                      className: cx((_cx2 = {}, _defineProperty(_cx2, "".concat(prefix, "-table__column"), true), _defineProperty(_cx2, "".concat(prefix, "-table__column--sortable"), column.canSort && column.allowSort), _defineProperty(_cx2, "".concat(prefix, "-table__column--sorted-asc"), column.isSorted && !column.isSortedDesc), _defineProperty(_cx2, "".concat(prefix, "-table__column--sorted-desc"), column.isSorted && column.isSortedDesc), _cx2))
                    }), _jsx("span", {
                      className: "".concat(prefix, "-table__column-name"),
                      children: column.render('Header')
                    }), column.tooltip && _jsx("span", {
                      className: "".concat(prefix, "-table__column-tooltip"),
                      children: _jsx(Tooltip, {
                        content: column.tooltip,
                        children: _jsx(Icon, {
                          size: 14,
                          icon: "help",
                          color: 'var(--dentsu-color-n650)'
                        })
                      })
                    }), column.canSort && column.allowSort && _jsxs("span", {
                      className: "".concat(prefix, "-table__column-icon"),
                      children: [column.canSort && !column.isSorted && _jsx(Icon, {
                        icon: "double-caret-vertical",
                        color: 'var(--dentsu-color-n500)'
                      }), column.canSort && column.isSorted && !column.isSortedDesc && _jsx(Icon, {
                        icon: "caret-up",
                        color: 'var(--dentsu-color-blue)'
                      }), column.canSort && column.isSorted && column.isSortedDesc && _jsx(Icon, {
                        icon: "caret-down",
                        color: 'var(--dentsu-color-blue)'
                      })]
                    }));
                  })
                }));
              })
            }), _jsxs("tbody", _objectSpread(_objectSpread({}, getTableBodyProps()), {}, {
              children: [isLoading && Array.from({
                length: defaultPageSize
              }, function (_, rowIndex) {
                return _jsx("tr", {
                  "data-row": rowIndex,
                  children: visibleColumns.map(function (columns, columnIndex) {
                    if (columns.skeleton) {
                      return _jsx("td", {
                        children: _jsx("div", {
                          style: {
                            opacity: 1 - rowIndex / (defaultPageSize + 5)
                          },
                          children: columns.skeleton
                        })
                      }, columnIndex);
                    } else {
                      return _jsx("td", {
                        children: _jsx(Skeleton, {
                          style: {
                            opacity: 1 - rowIndex / (defaultPageSize + 5)
                          }
                        })
                      }, columnIndex);
                    }
                  })
                }, rowIndex);
              }), hasEmptyResult ? !isLoading && _jsx("tr", {
                children: _jsx("td", {
                  colSpan: allColumns.length,
                  children: _jsx(EmptyState, {
                    layout: "vertical",
                    size: "small",
                    image: null,
                    title: noResultsText.title,
                    children: noResultsText.description && _jsx(Paragraph, {
                      children: noResultsText.description
                    })
                  })
                })
              }) : rows.length === 0 && !isLoading && _jsx("tr", {
                children: _jsx("td", {
                  colSpan: allColumns.length,
                  children: rows.length < preFilteredRows.length ? _jsx(EmptyState, {
                    layout: "vertical",
                    size: "small",
                    image: null,
                    title: noResultsText.title,
                    children: noResultsText.description && _jsx(Paragraph, {
                      children: noResultsText.description
                    })
                  }) : emptyState
                })
              }), !isLoading && !hasPagination && rowData.map(function (row, i) {
                prepareRow(row);

                var _loop = function _loop(j) {
                  var cell = row.allCells[j];
                  var rowSpanHeader = rowSpanHeaders.find(function (x) {
                    return x.id === cell.column.id;
                  });

                  if (rowSpanHeader !== undefined) {
                    if (rowSpanHeader.topCellValue === null || rowSpanHeader.topCellValue !== cell.value) {
                      cell.isRowSpanned = false;
                      rowSpanHeader.topCellValue = cell.value;
                      rowSpanHeader.topCellIndex = i;
                      cell.rowSpan = 1;
                    } else {
                      rows[rowSpanHeader.topCellIndex].allCells[j].rowSpan++;
                      cell.isRowSpanned = true;
                    }
                  }
                };

                for (var j = 0; j < row.allCells.length; j++) {
                  _loop(j);
                }

                return null;
              }), !isLoading && rowData.map(function (row, i) {
                var _cx3, _cx4;

                if (hasPagination) prepareRow(row);
                return _jsxs(React.Fragment, {
                  children: [_jsx("tr", _objectSpread(_objectSpread({
                    className: cx((_cx3 = {}, _defineProperty(_cx3, "".concat(prefix, "-table-row"), true), _defineProperty(_cx3, "".concat(prefix, "-table-row--hide-column-border"), canHideColumnBorder), _cx3)),
                    "data-selected": row.isSelected,
                    "data-expanded": row.isExpanded
                  }, row.getRowProps()), {}, {
                    children: row.cells.map(function (cell, i) {
                      var _cell$column$type;

                      if (cell.isRowSpanned) return null;
                      return _createElement("td", _objectSpread(_objectSpread({}, cell.getCellProps()), {}, {
                        key: i,
                        rowSpan: cell.rowSpan,
                        "data-type": (_cell$column$type = cell.column.type) !== null && _cell$column$type !== void 0 ? _cell$column$type : 'text',
                        style: cell.column.type !== 'menu' && cell.column.type !== 'select' && cell.column.type !== 'expander' && cell.column.type !== 'icon' ? {
                          width: cell.column.width,
                          minWidth: cell.column.minWidth,
                          maxWidth: cell.column.maxWidth,
                          verticalAlign: cell.column.verticalAlignment || 'middle'
                        } : {}
                      }), cell.column.prefix && _jsx(StyledTablePrefix, {
                        children: cell.column.prefix
                      }), cell.column.type === 'menu' || cell.column.type === 'select' || cell.column.type === 'expander' || cell.column.type === 'status' || cell.column.type === 'custom' ? cell.render('Cell') : renderContent(cell.value, cell.column.type, cell.column.options), cell.column.suffix && _jsx(StyledTableSuffix, {
                        children: cell.column.suffix
                      }));
                    })
                  })), row.isExpanded && !row.canExpand ? _jsx("tr", {
                    className: cx((_cx4 = {}, _defineProperty(_cx4, "".concat(prefix, "-table-row"), true), _defineProperty(_cx4, "".concat(prefix, "-table-row--expandable"), true), _defineProperty(_cx4, "".concat(prefix, "-table-row--selectable"), isSelectable), _cx4)),
                    "data-expanded": row.isExpanded,
                    "data-selected": row.isSelected,
                    children: _jsx("td", {
                      colSpan: allColumns.length,
                      children: renderRowSubComponent({
                        row: row
                      })
                    })
                  }) : null]
                }, i);
              })]
            })), showFooter && _jsx("tfoot", {
              children: footerGroups.map(function (footerGroup) {
                var _cx5;

                return _jsx("tr", _objectSpread(_objectSpread({
                  className: cx((_cx5 = {}, _defineProperty(_cx5, "".concat(prefix, "-table-row"), true), _defineProperty(_cx5, "".concat(prefix, "-table-row--hide-column-border"), canHideColumnBorder), _cx5))
                }, footerGroup.getFooterGroupProps()), {}, {
                  children: footerGroup.headers.map(function (column, i) {
                    var _column$type;

                    return _createElement("td", _objectSpread(_objectSpread({}, column.getFooterProps()), {}, {
                      key: i,
                      "data-type": (_column$type = column.type) !== null && _column$type !== void 0 ? _column$type : 'text'
                    }), column.Footer.length !== 0 && _jsxs(_Fragment, {
                      children: [column.prefix && _jsx(StyledTablePrefix, {
                        children: column.prefix
                      }), _jsx("span", {
                        className: "".concat(prefix, "-table__column-name"),
                        children: column.type === 'status' || column.type === 'custom' ? column.render('Footer') : renderContent(column.Footer, column.type, column.options)
                      }), column.suffix && _jsx(StyledTableSuffix, {
                        children: column.suffix
                      })]
                    }));
                  })
                }));
              })
            })]
          }))
        })
      }), hasPagination && _jsxs(StyledTablePagination, {
        children: [_jsx(StyledTablePaginationOptions, {
          children: _jsxs(Stack, {
            alignItems: "center",
            children: [_jsxs(StyledTablePaginationSelection, {
              children: [showPerPageText, ":"]
            }), _jsx(Dropdown, {
              style: {
                marginLeft: 8,
                marginRight: 8
              },
              value: pageSize,
              onChange: function onChange(e) {
                setPageSize(Number(e.target.value));
              },
              children: pageSizeOptions.map(function (pageSize) {
                return _jsx(Dropdown.Option, {
                  value: pageSize,
                  children: pageSize
                }, pageSize);
              })
            })]
          })
        }), (isManualPagination && total !== 0 || !isManualPagination && rows !== 0) && _jsx(StyledTablePaginationNavigation, {
          children: _jsxs(Stack, {
            alignItems: "center",
            children: [pageText, _jsx(Dropdown, {
              style: {
                marginLeft: 8,
                marginRight: 8
              },
              value: pageIndex,
              isDisabled: pageOptions.length === 1,
              onChange: function onChange(e) {
                gotoPage(Number(e.target.value));
              },
              children: pageOptions.fill(undefined).map(function (value, index) {
                return _jsx(Dropdown.Option, {
                  value: index,
                  children: index + 1
                }, index);
              })
            }), ofText, " ", pageOptions.length, _jsxs(StyledTablePaginationButtons, {
              children: [_jsx(IconButton, {
                icon: "arrow-left",
                variant: "ghost",
                size: "small",
                isDisabled: !canPreviousPage,
                onClick: function onClick() {
                  if (!canPreviousPage) return;
                  previousPage();
                }
              }), _jsx(IconButton, {
                icon: "arrow-right",
                variant: "ghost",
                size: "small",
                isDisabled: !canNextPage,
                onClick: function onClick() {
                  if (!canNextPage) return;
                  nextPage();
                }
              })]
            })]
          })
        })]
      })]
    }))
  });
});
export default TableNext;